import { message } from "antd";
import { useEffect, useState } from "react";
import { api } from "../api";
import { Button } from "./tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "./tailwind/dialog";
import { Field, Legend } from "./tailwind/fieldset";
import { Input } from "./tailwind/input";
import { Text } from "./tailwind/text";

function ZendeskIntegration({ customer, plainTitle = false }) {
  const [hostZendesk, setHostZendesk] = useState("");
  const [zendeskOAuthClientId, setZendeskOAuthClientId] = useState("");
  const [zendeskOAuthClientSecret, setZendeskOAuthClientSecret] = useState("");
  const [isConnectZendeskLoading, setIsConnectZendeskLoading] = useState(false);
  const [showZendeskDisconnectModal, setShowZendeskDisconnectModal] = useState(false);

  useEffect(() => {
    if (customer?.hostZendesk) setHostZendesk(customer.hostZendesk);
    if (customer?.zendeskOAuthClientId) setZendeskOAuthClientId(customer.zendeskOAuthClientId);
    if (customer?.zendeskOAuthClientSecret) setZendeskOAuthClientSecret(customer.zendeskOAuthClientSecret);
  }, [customer]);

  const handleConnectZendesk = async () => {
    if (!hostZendesk || !zendeskOAuthClientId || !zendeskOAuthClientSecret) {
      message.error(
        "Please fill in your Zendesk host, OAuth client ID and secret first.",
      );
      return;
    }

    setIsConnectZendeskLoading(true);
    try {
      // Save the Zendesk connection details before starting the OAuth flow,
      // so the backend always has the values entered in this form.
      await api.customer.update(customer.id, {
        hostZendesk,
        zendeskOAuthClientId,
        zendeskOAuthClientSecret,
      });

      const installationUrl = await api.generateZendeskInstallationUrl({
        customerId: customer.id,
        returnTo: window.location.pathname + window.location.search,
      });
      window.open(installationUrl, "_blank");
    } catch (error) {
      console.error("Error connecting to Zendesk:", error);
      message.error(
        "Could not connect to Zendesk. Please check your host, OAuth client ID and secret.",
      );
    } finally {
      setIsConnectZendeskLoading(false);
    }
  };

  const handleConfirmZendeskDisconnect = async () => {
    setIsConnectZendeskLoading(true);
    try {
      await api.zendesk.disconnect();
    } catch (e) {
      console.error(e);
    } finally {
      setIsConnectZendeskLoading(false);
      setShowZendeskDisconnectModal(false);
      // Reload in place rather than redirecting to the settings page, so this
      // works the same whether triggered from settings or from onboarding.
      window.location.reload();
    }
  };

  return (
    <>
      <Field className={plainTitle ? "mb-2" : undefined}>
        {plainTitle ? (
          <Text className="font-bold">Zendesk</Text>
        ) : (
          <Legend>Zendesk</Legend>
        )}
        {customer?.zendeskAccessToken ? (
          <>
            <Text className="mb-2">Your account is linked to Zendesk</Text>
            <Input
              className="mb-2"
              name="Zendesk host"
              value={hostZendesk}
              disabled
              autoComplete="off"
            />
            <Input
              className="mb-2"
              name="Zendesk OAuth client ID"
              value={zendeskOAuthClientId}
              disabled
              autoComplete="off"
            />
            <Input
              className="mb-2"
              name="Zendesk OAuth client secret"
              value={zendeskOAuthClientSecret}
              disabled
              autoComplete="off"
              type="password"
            />
            <Button
              color="secondaryButton"
              loading={isConnectZendeskLoading}
              onClick={() => setShowZendeskDisconnectModal(true)}
            >
              Disconnect Zendesk
            </Button>
          </>
        ) : (
          <>
            {customer?.apiKeyZendesk && (
              <div className="text-sm text-amber-800 mb-3 bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="font-semibold mb-1">
                  Your Zendesk connection needs to be updated
                </p>
                <p>
                  You're currently connected to Zendesk using an older
                  method (API token) that Zendesk is discontinuing on{" "}
                  <span className="font-medium">April 30, 2027</span>. Your
                  integration still works today, but to keep it working you
                  need to set up the new connection below before that date.
                </p>
              </div>
            )}
            <div className="text-sm text-gray-600 mb-3 bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="font-semibold mb-1">
                How to connect Zendesk (one-time setup, ~2 minutes):
              </p>
              <ol className="list-decimal list-inside space-y-1">
                <li>
                  In Zendesk, go to{" "}
                  <span className="font-medium">
                    Admin Center &gt; Apps and integrations &gt; APIs &gt;
                    OAuth clients
                  </span>{" "}
                  and click <span className="font-medium">Add OAuth client</span>.
                </li>
                <li>
                  Set <span className="font-medium">Client kind</span> to{" "}
                  <span className="font-medium">Confidential</span>, and add
                  the redirect URL below in{" "}
                  <span className="font-medium">Redirect URLs</span> (copy it
                  exactly, this must match or Zendesk will reject the
                  connection):
                </li>
              </ol>
              <div className="flex flex-row items-center mt-2 mb-2">
                <Input
                  value={`${window.location.origin}/zendesk/installation`}
                  readOnly
                  autoComplete="off"
                />
                <Button
                  color="optionButton"
                  className="ml-2"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/zendesk/installation`,
                    );
                    message.success("Copied to clipboard");
                  }}
                >
                  Copy
                </Button>
              </div>
              <ol className="list-decimal list-inside space-y-1" start={3}>
                <li>
                  Save the client in Zendesk, then copy the{" "}
                  <span className="font-medium">Identifier</span> (this is
                  the "OAuth client ID" below) and the{" "}
                  <span className="font-medium">Secret</span> (shown only
                  once — copy it now) into the fields below.
                </li>
                <li>
                  Fill in your Zendesk host: this is your Zendesk web address
                  without "https://" and without "www." (e.g. if you access
                  Zendesk at{" "}
                  <span className="font-medium">
                    https://mycompany.zendesk.com
                  </span>
                  , enter <span className="font-medium">mycompany.zendesk</span>
                  ).
                </li>
              </ol>
            </div>
            <Input
              className="mb-2"
              name="Zendesk host"
              placeholder="e.g., mycompany.zendesk"
              value={hostZendesk}
              onChange={(e) => setHostZendesk(e.target.value)}
              autoComplete="off"
              maxLength={200}
            />
            <Input
              className="mb-2"
              name="Zendesk OAuth client ID"
              placeholder="OAuth client ID (Identifier)"
              value={zendeskOAuthClientId}
              onChange={(e) => setZendeskOAuthClientId(e.target.value)}
              autoComplete="off"
              maxLength={500}
            />
            <Input
              className="mb-2"
              name="Zendesk OAuth client secret"
              placeholder="OAuth client secret"
              value={zendeskOAuthClientSecret}
              onChange={(e) => setZendeskOAuthClientSecret(e.target.value)}
              autoComplete="off"
              maxLength={500}
              type="password"
            />
            <Button
              color="secondaryButton"
              loading={isConnectZendeskLoading}
              disabled={
                !hostZendesk || !zendeskOAuthClientId || !zendeskOAuthClientSecret
              }
              onClick={handleConnectZendesk}
            >
              Connect Zendesk
            </Button>
          </>
        )}
      </Field>
      <Dialog
        open={showZendeskDisconnectModal}
        onClose={() => setShowZendeskDisconnectModal(false)}
      >
        <DialogTitle>Disconnect Zendesk</DialogTitle>
        <DialogBody>
          <p className="mb-3">
            You're about to disconnect your Zendesk account from Simio. This
            stops Simio from creating or updating Zendesk tickets.
          </p>
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="font-semibold mb-1">
              This does not stop Zendesk from sending updates to Simio
            </p>
            <p>
              Ticket updates are sent to Simio by a Trigger configured
              inside your Zendesk account, independently of this
              connection. To fully stop this, go to your Zendesk{" "}
              <span className="font-medium">
                Admin Center &gt; Objects and rules &gt; Business rules &gt;
                Triggers
              </span>
              , find the trigger that notifies Simio (it posts to a URL
              containing your Simio account), and deactivate or delete it.
            </p>
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={() => setShowZendeskDisconnectModal(false)}
            disabled={isConnectZendeskLoading}
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={handleConfirmZendeskDisconnect}
            loading={isConnectZendeskLoading}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ZendeskIntegration;
